import type { ReactNode } from 'react';
import Dialog from '@mui/material/Dialog';
import InputBase from '@mui/material/InputBase';
import type { DecoratedTaskTemplate } from '@witmetrics/utils';
import { isEmpty } from '@witmetrics/utils/misc';
import {
  useActivePractice,
  usePracticeTaskTemplates,
} from '@witmetrics/data-store';
import { useString } from '@/hooks/useString';
import SlideTransition from '@/components/SlideTransition';
import ResultsList from './ResultsList';
import { classes, filterTemplates } from './utils';

export type TaskTemplateSearchDialogProps = {
  onSelect: (template: DecoratedTaskTemplate) => void;
  onClose: () => void;
};

export default function TaskTemplateSearchDialog({
  onSelect,
  onClose,
}: TaskTemplateSearchDialogProps) {
  const activePractice = useActivePractice();
  const templates = usePracticeTaskTemplates(activePractice?.id);
  const [search, setSearch] = useString();

  const handleSelect = (template: DecoratedTaskTemplate) => {
    onSelect(template);
    onClose();
  };

  const renderMessage = (message: ReactNode) => (
    <div className={classes.message}>{message}</div>
  );

  const renderResults = () => {
    if (isEmpty(templates)) {
      return renderMessage(
        `No task templates found for ${activePractice?.name ?? 'this practice'}.`
      );
    }
    const results = filterTemplates(templates, search);
    if (isEmpty(results)) {
      return renderMessage('No templates matching current search.');
    }
    return <ResultsList results={results} onSelect={handleSelect} />;
  };

  return (
    <Dialog
      open
      keepMounted
      disableRestoreFocus
      maxWidth={false}
      PaperProps={{ className: classes.paper }}
      TransitionComponent={SlideTransition}
      onClose={onClose}>
      <div className={classes.wrapper}>
        <InputBase
          autoFocus
          className={classes.inputBase}
          autoComplete="off"
          name="task-template-search"
          placeholder="Search for templates by description..."
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {renderResults()}
      </div>
    </Dialog>
  );
}
